import * as React from 'react';
import Stack from '@mui/material/Stack';
import TrapFocus from '@mui/material/Unstable_TrapFocus';
import CssBaseline from '@mui/material/CssBaseline';
import Paper from '@mui/material/Paper';
import Fade from '@mui/material/Fade';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import * as formatters from '../../utils/formatters';
import styled from '@emotion/styled';
import PaymentDialog from '../MakeAPayment/Dialogs/PaymentDialog';

const bannerSessionKey = 'OverdueInvoicesBannerOpen';

const ValueBlock = styled.span`
    font-weight: bold;
`;
const ACCOUNT_PAID = 'PAID';
const BANNER_DISMISSED = 'DISMISSED';

export default function OverdueInvoicesBanner({ account, user }) {
    const prevAccountNumberRef = React.useRef();

    const itemKey = `${bannerSessionKey}_${account?.AccountNumber}`;

    const [showPayDialog, setShowPayDialog] = React.useState(false);

    const [bannerStatus, setBannerStatus] = React.useState(() => {
        const savedState = sessionStorage.getItem(itemKey);
        return savedState !== null ? JSON.parse(savedState) : null;
    });

    React.useEffect(() => {
        // Save the state to session storage whenever it changes
        if (!account?.AccountNumber) return;
        if (prevAccountNumberRef.current !== account.AccountNumber) {
            prevAccountNumberRef.current = account.AccountNumber;
            const savedState = sessionStorage.getItem(itemKey);
            setBannerStatus(savedState !== null ? JSON.parse(savedState) : null);
        }

        if (bannerStatus && bannerStatus.indexOf(account.AccountNumber) !== -1) {
            sessionStorage.setItem(itemKey, JSON.stringify(bannerStatus));
        }
    }, [itemKey, bannerStatus, account?.AccountNumber]);

    const closeBanner = () => {
        setBannerStatus(BANNER_DISMISSED + account?.AccountNumber);
    };

    const onMakePaymentClick = () => {
        setShowPayDialog(true);
        return false;
    };

    const amountOverdue = formatters.currencyFormatter({
        value: account?.AmountOverdue ?? '',
    });

    const showBanner =
        account?.PastDueDays > 0 &&
        bannerStatus !== BANNER_DISMISSED + account.AccountNumber &&
        bannerStatus !== ACCOUNT_PAID;
    if (!showBanner) return null;
    return (
        <React.Fragment>
            <CssBaseline />

            <TrapFocus open disableAutoFocus disableEnforceFocus>
                <Fade appear={false} in={showBanner}>
                    <Paper
                        role="dialog"
                        aria-modal="false"
                        aria-label="Cookie banner"
                        square
                        variant="outlined"
                        tabIndex={-1}
                        sx={{
                            position: 'fixed',

                            top: 0,
                            left: 0,
                            right: 0,
                            m: 0,
                            p: 2,
                            borderWidth: 1,
                            fontSize: '1.5rem',
                            backgroundColor: 'white',
                            color: 'red',
                            padding: '1rem',
                            margin: '1rem 0',
                            border: '1px solid black',
                            zIndex: (theme) => theme.zIndex.drawer + 1,
                            borderTopWidth: 1,
                        }}
                    >
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2}>
                            <Box
                                sx={{
                                    flexShrink: 1,
                                    alignSelf: { xs: 'flex-start', sm: 'center' },
                                }}
                            >
                                <Typography fontWeight="bold">
                                    Your account <ValueBlock>#{account?.AccountNumber}</ValueBlock> is{' '}
                                    <ValueBlock>{account?.PastDueDays ?? ''}</ValueBlock> days past due. To bring your
                                    account current, make a payment of <ValueBlock>{amountOverdue}</ValueBlock>
                                </Typography>
                            </Box>
                            <Stack
                                gap={2}
                                direction={{
                                    xs: 'row-reverse',
                                    sm: 'row',
                                }}
                                sx={{
                                    flexShrink: 0,
                                    alignSelf: { xs: 'flex-end', sm: 'center' },
                                }}
                            >
                                <Button size="small" onClick={onMakePaymentClick} variant="contained" color="success">
                                    Make a payment
                                </Button>
                                <Button size="small" onClick={closeBanner} color="info">
                                    Dismiss
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                </Fade>
            </TrapFocus>
            <PaymentDialog
                title="Make a payment"
                accountNumber={account?.AccountNumber}
                paymentAmount={account?.AmountOverdue}
                user={user}
                isVisible={showPayDialog}
                onPaymentSuccess={() => {
                    setBannerStatus(ACCOUNT_PAID);
                    // setShowPayDialog(false);
                }}
                onClose={() => {
                    // setBannerStatus(ACCOUNT_PAID); //TODO: This should be set to the account status on successful payment
                    setShowPayDialog(false);
                }}
            ></PaymentDialog>
        </React.Fragment>
    );
}
